/**
 * 统一请求封装 - Axios版本
 * 支持JWT认证和错误处理
 */
import axios from 'axios'
import { showToast, showLoadingToast, closeToast } from 'vant'
import i18n from '@/i18n'
import { useUserStore } from '@/stores/user'
import { useLanguageStore } from '@/stores/language'
import router from '@/router'
import tokenManager from './auth/tokenManager'
import { CLIENT_ENTRY_HEADER, CLIENT_ENTRY_VALUE } from './client-entry'

// 基础URL策略：
// - 开发环境使用 '/api'，由 Vite 代理到后端，避免跨域
// - 生产环境优先使用 VITE_AXIOS_BASE_URL（如未设置则回退到 '/api'，配合 Nginx 反代）
const resolveBaseURL = () => {
  if (import.meta.env.DEV) return '/'
  return import.meta.env.VITE_AXIOS_BASE_URL || '/api'
}

// 创建axios实例
const service = axios.create({
  baseURL: resolveBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
service.interceptors.request.use(
  async config => {
    // 如果请求标记为跳过认证（如刷新token请求），则跳过token处理
    if (!config.skipAuth) {
      try {
        // 尝试获取有效的token（会自动刷新）
        const token = await tokenManager.getValidAccessToken()
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`
        }
      } catch (error) {
        // token获取失败，让请求继续，由响应拦截器处理401
        console.warn('Token获取失败，继续发送请求:', error.message)
      }
    }
    if (!config.headers) {
      config.headers = {}
    }
    config.headers[CLIENT_ENTRY_HEADER] = CLIENT_ENTRY_VALUE
    const languageStore = useLanguageStore()
    // 显示loading - 使用当前语言
    if (config.showLoading !== false) {
      // 获取当前语言的翻译文本
      const currentLocale = languageStore?.currentUiLanguage || i18n.global.locale.value || 'zh-CN'
      const loadingText = config.loadingText ||
        (i18n.global.messages[currentLocale]?.common?.loading || '加载中...')

      showLoadingToast({
        message: loadingText,
        forbidClick: true,
        duration: 0
      })
    }

    return config
  },
  error => {
    closeToast()
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    closeToast()
    return response.data
  },
  async error => {
    closeToast()

    if (error.response) {
      const { status, data, config } = error.response
      const userStore = useUserStore()
      const languageStore = useLanguageStore()

      // 获取当前语言
      const currentLocale = languageStore?.currentUiLanguage || i18n.global.locale.value || 'zh-CN'
      const messages = i18n.global.messages[currentLocale] || i18n.global.messages['zh-CN']

      // 获取翻译文本的辅助函数
      const t = (key, fallback) => {
        const keys = key.split('.')
        let value = messages
        for (const k of keys) {
          value = value?.[k]
        }
        return value || fallback
      }

      switch (status) {
        case 401:
          // 跳过认证的请求（例如刷新token本身）不做自动刷新处理，避免递归/死锁
          if (config.skipAuth) {
            break
          }
          // Token过期，尝试刷新一次
          if (!config._retry) {
            config._retry = true // 标记已重试，防止无限循环
            try {
              // 尝试刷新token
              await tokenManager.refreshAccessToken()
              // 刷新成功，重试原请求
              const token = tokenManager.getTokens().accessToken
              if (token) {
                config.headers['Authorization'] = `Bearer ${token}`
              }
              return service(config)
            } catch (refreshError) {
              // 刷新失败，清除token并跳转登录
              tokenManager.clearTokens()
              userStore.logout()
              router.push('/login')
              showToast(i18n.global.t('error.auth'))
            }
          } else {
            // 已经重试过，直接跳转登录
            tokenManager.clearTokens()
            userStore.logout()
            router.push('/login')
            showToast(t('error.auth', '认证失败，请重新登录'))
          }
          break
        case 403:
          showToast(t('error.forbidden', '没有权限访问'))
          break
        case 404:
          showToast(t('error.notFound', '请求的资源不存在'))
          break
        case 500:
          showToast(t('error.server', '服务器错误，请稍后重试'))
          break
        default:
          const errorMsg = data?.detail || data?.message || t('error.requestFailed', '请求失败')
          if (error.config?.showError !== false) {
            showToast(errorMsg)
          }
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      const languageStore = useLanguageStore()
      const currentLocale = languageStore?.currentUiLanguage || i18n.global.locale.value || 'zh-CN'
      const messages = i18n.global.messages[currentLocale] || i18n.global.messages['zh-CN']
      const networkError = messages?.error?.network || '网络错误，请检查网络连接'
      showToast(networkError)
    } else {
      // 请求配置出错
      const languageStore = useLanguageStore()
      const currentLocale = languageStore?.currentUiLanguage || i18n.global.locale.value || 'zh-CN'
      const messages = i18n.global.messages[currentLocale] || i18n.global.messages['zh-CN']
      const configError = messages?.error?.configError || '请求配置错误'
      showToast(configError)
    }

    return Promise.reject(error)
  }
)

// 封装请求方法类
class Request {
  /**
   * GET请求
   */
  get(url, params = {}, config = {}) {
    return service({
      url,
      method: 'GET',
      params,
      ...config
    })
  }

  /**
   * POST请求
   */
  post(url, data = {}, config = {}) {
    return service({
      url,
      method: 'POST',
      data,
      ...config
    })
  }

  /**
   * PUT请求
   */
  put(url, data = {}, config = {}) {
    return service({
      url,
      method: 'PUT',
      data,
      ...config
    })
  }

  /**
   * DELETE请求
   */
  delete(url, params = {}, config = {}) {
    return service({
      url,
      method: 'DELETE',
      params,
      ...config
    })
  }

  /**
   * 文件上传
   */
  upload(url, file, formData = {}, config = {}) {
    const data = new FormData()
    data.append('file', file)

    // 添加其他表单数据
    Object.keys(formData).forEach(key => {
      data.append(key, formData[key])
    })

    return service({
      url,
      method: 'POST',
      data,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    })
  }

  /**
   * 批量请求
   */
  all(requests) {
    return Promise.all(requests)
  }
}

// 导出请求实例
const request = new Request()

// 同时导出axios实例，方便特殊场景使用
export { service as axios }

export default request
